import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { REGISTRATION_STEPS, FLOW_TYPES } from "./constants";

const GeniusRegistrationContext = createContext(null);

const initialShared = () => ({
  mobile: "",
  otpVerified: false,
  whatsappConsent: true,
  termsAccepted: false
});

export const GeniusRegistrationProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isBrochureOpen, setIsBrochureOpen] = useState(false);
  const [step, setStep] = useState(REGISTRATION_STEPS.INTENT);
  const [flowType, setFlowType] = useState(null);
  const [shared, setShared] = useState(initialShared);

  const reset = useCallback(() => {
    setStep(REGISTRATION_STEPS.INTENT);
    setFlowType(null);
    setShared(initialShared());
  }, []);

  const closeRegistration = useCallback(() => {
    setIsOpen(false);
    reset();
  }, [reset]);

  const closeBrochure = useCallback(() => {
    setIsBrochureOpen(false);
  }, []);

  const openBrochure = useCallback(() => {
    setIsOpen(false);
    reset();
    setIsBrochureOpen(true);
  }, [reset]);

  const openRegistration = useCallback((options = {}) => {
    const { initialFlow, skipIntent = false } = options;
    setIsBrochureOpen(false);
    setShared(initialShared());
    setIsOpen(true);

    if (skipIntent && initialFlow === FLOW_TYPES.SCHOOL) {
      setFlowType(FLOW_TYPES.SCHOOL);
      setStep(REGISTRATION_STEPS.PHONE);
      return;
    }

    if (initialFlow === FLOW_TYPES.STUDENT || initialFlow === FLOW_TYPES.SCHOOL) {
      setFlowType(initialFlow);
      setStep(REGISTRATION_STEPS.PHONE);
      return;
    }

    setFlowType(null);
    setStep(REGISTRATION_STEPS.INTENT);
  }, []);

  const selectFlow = useCallback((type) => {
    setFlowType(type);
    setStep(REGISTRATION_STEPS.PHONE);
  }, []);

  const goToOtp = useCallback(() => {
    setStep(REGISTRATION_STEPS.OTP);
  }, []);

  const goToForm = useCallback(() => {
    setShared((prev) => ({ ...prev, otpVerified: true }));
    setStep(
      flowType === FLOW_TYPES.SCHOOL
        ? REGISTRATION_STEPS.SCHOOL_FORM
        : REGISTRATION_STEPS.STUDENT_FORM
    );
  }, [flowType]);

  const goToSuccess = useCallback(() => {
    setStep(REGISTRATION_STEPS.SUCCESS);
  }, []);

  const updateShared = useCallback((patch) => {
    setShared((prev) => ({ ...prev, ...patch }));
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      isBrochureOpen,
      step,
      flowType,
      shared,
      openRegistration,
      openBrochure,
      closeBrochure,
      closeRegistration,
      selectFlow,
      goToOtp,
      goToForm,
      goToSuccess,
      updateShared,
      setStep
    }),
    [
      isOpen,
      isBrochureOpen,
      step,
      flowType,
      shared,
      openRegistration,
      openBrochure,
      closeBrochure,
      closeRegistration,
      selectFlow,
      goToOtp,
      goToForm,
      goToSuccess,
      updateShared
    ]
  );

  return (
    <GeniusRegistrationContext.Provider value={value}>
      {children}
    </GeniusRegistrationContext.Provider>
  );
};

export const useGeniusRegistration = () => {
  const ctx = useContext(GeniusRegistrationContext);
  if (!ctx) {
    throw new Error("useGeniusRegistration must be used within GeniusRegistrationProvider");
  }
  return ctx;
};
